import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/aevos/PageHero";
import { CheckCircle2, GraduationCap, FlaskConical } from "lucide-react";

const FORMACAO = [
  "Médico pela Universidade de Mogi das Cruzes (UMC) — conclusão em 2018",
  "Residência Médica no Hospital Sírio-Libanês",
];

const POS_GRAD = [
  "Pós-graduação em Anabolismo Clínico",
  "Pós-graduação em Medicina do Esporte",
  "Pós-graduação em Nutrição Estética, Esportiva e Saúde da Mulher",
  "Pós-graduação em Hormônios e Saúde da Mulher",
];

const APERFEICOAMENTOS = [
  { area: "Farmacologia Hormonal", prof: "Prof. Gabriel Kaminski" },
  { area: "Ergogênicos, Nutrição e Treinamento Feminino", prof: "Prof. Adam Abbas" },
];

const INTERESSES = [
  "Saúde Metabólica",
  "Medicina Preventiva",
  "Composição Corporal",
  "Performance Física",
  "Medicina do Esporte",
  "Saúde da Mulher",
  "Longevidade Saudável",
  "Estratégias de Mudança de Estilo de Vida",
];

const TEAM = [
  {
    initials: "GA",
    name: "Dr. Guilherme Affonso",
    role: "Médico responsável · Medicina Preventiva",
    desc: "CRM-SP 200364 · RQE 120948 · Residência no Hospital Sírio-Libanês",
  },
  {
    initials: "CB",
    name: "Dra. Camila Barros",
    role: "Nutricionista clínica",
    desc: "CRN3 80684 · Pós-graduada em Nutrição Esportiva e Estética",
  },
  {
    initials: "EP",
    name: "Equipe parceira",
    role: "Personal trainers + laboratórios",
    desc: "Rede selecionada para protocolos integrados.",
  },
];

const FormacaoSection = () => (
  <div className="mt-10">
    <div className="flex items-center gap-3 mb-4">
      <GraduationCap size={20} className="text-[#00C4FF]" />
      <p className="aevos-eyebrow">Formação Médica</p>
    </div>
    <ul className="space-y-2">
      {FORMACAO.map((c) => (
        <li key={c} className="flex items-start gap-2 text-sm text-[#333]">
          <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0" />
          <span>{c}</span>
        </li>
      ))}
    </ul>
    <p className="mt-4 text-sm text-[#333]">
      <strong className="text-black">Especialidade:</strong> Medicina Preventiva e Social — RQE 120948
    </p>
  </div>
);

const PosGradSection = () => (
  <div className="mt-10">
    <div className="flex items-center gap-3 mb-4">
      <GraduationCap size={20} className="text-[#00C4FF]" />
      <p className="aevos-eyebrow">Pós-Graduações</p>
    </div>
    <ul className="grid sm:grid-cols-2 gap-2">
      {POS_GRAD.map((c) => (
        <li key={c} className="flex items-start gap-2 text-sm text-[#333]">
          <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0" />
          <span>{c}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AperfeicoamentosSection = () => (
  <div className="mt-10">
    <div className="flex items-center gap-3 mb-4">
      <FlaskConical size={20} className="text-[#00C4FF]" />
      <p className="aevos-eyebrow">Aperfeiçoamentos e Formação Complementar</p>
    </div>
    <ul className="space-y-3">
      {APERFEICOAMENTOS.map((c) => (
        <li key={c.area} className="flex items-start gap-2 text-sm text-[#333]">
          <CheckCircle2 size={16} className="text-[#00C4FF] mt-0.5 flex-shrink-0" />
          <span><strong className="text-black">{c.area}</strong> — <span className="text-[#666]">{c.prof}</span></span>
        </li>
      ))}
    </ul>
  </div>
);

const InteressesSection = () => (
  <div className="mt-10">
    <p className="aevos-eyebrow mb-4">Áreas de Interesse e Atuação</p>
    <div className="flex flex-wrap gap-2">
      {INTERESSES.map((it) => (
        <span key={it} className="px-3 py-1.5 border border-[#E0E0E0] text-sm text-[#333]">
          {it}
        </span>
      ))}
    </div>
  </div>
);

const TeamSection = () => (
  <section className="section-pad bg-[#E6F8FF] border-y border-[#E0E0E0]">
    <div className="aevos-container">
      <div className="aevos-eyebrow mb-3">Equipe</div>
      <h2 className="font-display font-bold text-3xl md:text-4xl text-black">Quem cuida de você na AEVOS.</h2>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {TEAM.map((t) => (
          <div key={t.initials} className="aevos-card p-6 bg-white">
            <div className="w-12 h-12 border border-black flex items-center justify-center font-display font-bold text-black">
              {t.initials}
            </div>
            <h3 className="font-display font-semibold text-base mt-4 text-black">{t.name}</h3>
            <p className="text-xs text-[#00C4FF] mt-1 uppercase tracking-wider font-semibold">{t.role}</p>
            <p className="text-sm text-[#333] mt-3">{t.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Sobre = () => {
  return (
    <div data-testid="page-sobre">
      <PageHero
        eyebrow="Sobre"
        title="Dr. Guilherme Affonso."
        subtitle="Medicina preventiva, performance e longevidade — acompanhamento clínico aprofundado, baseado em evidência e individualização."
      >
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="px-3 py-1.5 border border-black text-black font-display font-semibold tracking-wider">CRM-SP 200364</span>
          <span className="px-3 py-1.5 border border-black text-black font-display font-semibold tracking-wider">RQE 120948</span>
        </div>
      </PageHero>

      <section className="section-pad">
        <div className="aevos-container grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="aspect-[4/5] border border-[#E0E0E0] bg-[#E6F8FF] overflow-hidden sticky top-24">
              <img
                src="/assets/dr-guilherme.jpg"
                alt="Dr. Guilherme Affonso"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="aevos-eyebrow mb-3">Trajetória</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-black leading-[1.1]">
              Uma medicina que escuta a biologia, não apenas o sintoma.
            </h2>
            <div className="mt-6 space-y-4 text-[#333333]">
              <p>
                O Dr. Guilherme atua com foco na identificação e correção de fatores que impactam
                a saúde, energia, composição corporal e desempenho físico, utilizando uma abordagem
                preventiva e individualizada.
              </p>
              <p>
                Seu trabalho integra avaliação clínica, metabolismo, composição corporal, hábitos
                de vida e acompanhamento contínuo — buscando promover saúde sustentável e melhor
                qualidade de vida aos pacientes.
              </p>
            </div>

            <FormacaoSection />
            <PosGradSection />
            <AperfeicoamentosSection />
            <InteressesSection />
          </div>
        </div>
      </section>

      <TeamSection />

      <section className="pt-20 md:pt-28 pb-10 md:pb-14 bg-black text-white text-center">
        <div className="aevos-container">
          <h2 className="font-display font-bold text-3xl md:text-5xl max-w-3xl mx-auto leading-[1.1] !text-white">
            Quer iniciar com uma <span className="text-[#00C4FF]">avaliação clínica completa</span>?
          </h2>
          <div className="mt-8">
            <Link to="/contato"><span className="btn-cyan">Agendar Consulta</span></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sobre;
